import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, 
  Users, 
  Crown, 
  Shield, 
  ChevronLeft, 
  ChevronRight,
  RefreshCw,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  ArrowUpDown
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminUsers = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // Auth state
  const [isAdmin, setIsAdmin] = useState(null); // null = checking
  const [authError, setAuthError] = useState(null);
  
  // Data state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  
  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Auth headers
  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);
  
  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      if (!token) {
        setIsAdmin(false);
        setAuthError('Please log in to access this page');
        return;
      }
      
      try {
        const response = await axios.get(`${API_BASE}/auth/admin-check`, {
          headers: getAuthHeaders()
        });
        
        if (response.data.is_admin) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
          setAuthError('Admin access required');
        }
      } catch (err) {
        console.error('Admin check failed:', err);
        setIsAdmin(false);
        setAuthError('Failed to verify admin access');
      }
    };
    
    checkAdmin();
  }, [token, getAuthHeaders]);
  
  // Fetch user stats
  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    
    setStatsLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/users/stats/summary`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [isAdmin, getAuthHeaders]);
  
  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sort: sortOrder
      });
      
      if (debouncedSearch) {
        params.append('q', debouncedSearch);
      }
      if (planFilter) {
        params.append('plan', planFilter);
      }
      
      const response = await axios.get(`${API_BASE}/admin/users?${params}`, {
        headers: getAuthHeaders()
      });
      
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setIsAdmin(false);
        setAuthError('Admin access denied');
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, debouncedSearch, planFilter, sortOrder, getAuthHeaders]);
  
  // Initial load
  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin, fetchStats]);
  
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, fetchUsers]);
  
  // Redirect if not admin
  useEffect(() => {
    if (isAdmin === false) {
      const timer = setTimeout(() => {
        navigate('/app');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAdmin, navigate]);
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Render loading state
  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0f' }}>
        <div className="flex items-center gap-3">
          <RefreshCw className="h-6 w-6 animate-spin" style={{ color: '#60a5fa' }} />
          <span style={{ color: '#a1a1aa' }}>Verifying admin access...</span>
        </div>
      </div>
    );
  }
  
  // Render access denied
  if (isAdmin === false) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0f' }}>
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto mb-4" style={{ color: '#ef4444' }} />
          <h1 className="text-2xl font-bold mb-2" style={{ color: '#fafafa' }}>Access Denied</h1>
          <p className="mb-4" style={{ color: '#a1a1aa' }}>{authError || 'Admin access required'}</p>
          <p className="text-sm" style={{ color: '#71717a' }}>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0f' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6" style={{ color: '#60a5fa' }} />
              <h1 className="text-xl font-bold" style={{ color: '#fafafa' }}>User Management</h1>
              <span 
                className="text-xs px-2 py-1 rounded"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}
              >
                ADMIN
              </span>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="text-sm px-3 py-1.5 rounded hover:bg-white/5 transition-colors"
              style={{ color: '#a1a1aa' }}
            >
              ← Back to Admin
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" style={{ color: '#60a5fa' }} />
                <span className="text-xs" style={{ color: '#a1a1aa' }}>Total Users</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: '#fafafa' }}>{stats.totalUsers}</div>
            </div>
            
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-4 w-4" style={{ color: '#fbbf24' }} />
                <span className="text-xs" style={{ color: '#a1a1aa' }}>Pro Users</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: '#fbbf24' }}>{stats.proUsers}</div>
            </div>
            
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4" style={{ color: '#ef4444' }} />
                <span className="text-xs" style={{ color: '#a1a1aa' }}>Admins</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: '#ef4444' }}>{stats.adminUsers}</div>
            </div>
            
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4" style={{ color: '#4ade80' }} />
                <span className="text-xs" style={{ color: '#a1a1aa' }}>New (7 days)</span>
              </div>
              <div className="text-2xl font-bold" style={{ color: '#4ade80' }}>{stats.newUsersLast7Days}</div>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#71717a' }} />
            <input
              type="text"
              placeholder="Search by email or user ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg text-sm"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.03)', 
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fafafa'
              }}
              data-testid="user-search-input"
            />
          </div>
          
          {/* Plan Filter */}
          <select
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 rounded-lg text-sm"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.03)', 
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fafafa'
            }}
            data-testid="plan-filter-select"
          >
            <option value="">All Plans</option>
            <option value="pro">Pro</option>
            <option value="free">Free</option>
            <option value="admin">Admin</option>
          </select>
          
          {/* Sort */}
          <select
            value={sortOrder}
            onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
            className="px-4 py-2 rounded-lg text-sm"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.03)', 
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fafafa'
            }}
            data-testid="sort-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="email">Email A-Z</option>
          </select>
          
          {/* Refresh */}
          <button
            onClick={() => { fetchUsers(); fetchStats(); }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-white/5 transition-colors"
            style={{ 
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#a1a1aa'
            }}
            data-testid="refresh-button"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">Refresh</span>
          </button>
        </div>
        
        {/* Users Table (Desktop) */}
        <div className="hidden md:block rounded-lg overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <table className="w-full">
            <thead>
              <tr style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#a1a1aa' }}>Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#a1a1aa' }}>Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#a1a1aa' }}>Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#a1a1aa' }}>Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#a1a1aa' }}>Last Login</th>
                <th className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#a1a1aa' }}>Verified</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#71717a' }}>
                    <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center" style={{ color: '#71717a' }}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr 
                    key={user.id} 
                    className="border-t hover:bg-white/[0.02] transition-colors"
                    style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" style={{ color: '#71717a' }} />
                        <span className="text-sm" style={{ color: '#fafafa' }}>{user.email}</span>
                        {user.isAdmin && (
                          <Shield className="h-3.5 w-3.5" style={{ color: '#ef4444' }} title="Admin" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {user.isPro ? (
                        <span 
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                          style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24' }}
                        >
                          <Crown className="h-3 w-3" />
                          Pro
                        </span>
                      ) : (
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#a1a1aa' }}
                        >
                          Free
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ 
                          backgroundColor: user.subscriptionStatus === 'active' 
                            ? 'rgba(74, 222, 128, 0.1)' 
                            : 'rgba(255,255,255,0.05)',
                          color: user.subscriptionStatus === 'active' ? '#4ade80' : '#71717a'
                        }}
                      >
                        {user.subscriptionStatus || 'none'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#a1a1aa' }}>
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm" style={{ color: '#a1a1aa' }}>
                      {formatDate(user.lastLoginAt)}
                    </td>
                    <td className="px-4 py-3">
                      {user.emailVerified ? (
                        <CheckCircle className="h-4 w-4" style={{ color: '#4ade80' }} />
                      ) : (
                        <XCircle className="h-4 w-4" style={{ color: '#ef4444' }} />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Users Cards (Mobile) */}
        <div className="md:hidden space-y-3">
          {loading ? (
            <div className="text-center py-8" style={{ color: '#71717a' }}>
              <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8" style={{ color: '#71717a' }}>
              No users found
            </div>
          ) : (
            users.map((user) => (
              <div 
                key={user.id}
                className="p-4 rounded-lg"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" style={{ color: '#fafafa' }}>{user.email}</span>
                    {user.isAdmin && <Shield className="h-3.5 w-3.5" style={{ color: '#ef4444' }} />}
                  </div>
                  {user.isPro ? (
                    <span 
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                      style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24' }}
                    >
                      <Crown className="h-3 w-3" />
                      Pro
                    </span>
                  ) : (
                    <span 
                      className="text-xs px-2 py-1 rounded"
                      style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#a1a1aa' }}
                    >
                      Free
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs" style={{ color: '#71717a' }}>
                  <div>Created: {formatDate(user.createdAt)}</div>
                  <div>Last Login: {formatDate(user.lastLoginAt)}</div>
                  <div className="flex items-center gap-1">
                    Verified: {user.emailVerified ? (
                      <CheckCircle className="h-3 w-3" style={{ color: '#4ade80' }} />
                    ) : (
                      <XCircle className="h-3 w-3" style={{ color: '#ef4444' }} />
                    )}
                  </div>
                  <div>Status: {user.subscriptionStatus || 'none'}</div>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="text-sm" style={{ color: '#71717a' }}>
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!pagination.hasPrev}
                className="p-2 rounded hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: '#a1a1aa' }}
                data-testid="prev-page-button"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm" style={{ color: '#fafafa' }}>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                disabled={!pagination.hasNext}
                className="p-2 rounded hover:bg-white/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ color: '#a1a1aa' }}
                data-testid="next-page-button"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
